import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from '../utils/axios';
import { setSession } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  setting: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  SETTING: (state, action) => {
    const { setting } = action.payload;
    return {
      ...state,
      setting,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  VERIFY: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: 'verify',
      user,
    };
  },
  VERIFY_LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: 'verify_login',
      user,
    };
  },
  CHECK: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  verify: () => Promise.resolve(),
  verifyLogin: () => Promise.resolve(),
  refreshUser: () => Promise.resolve(null),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      const accessToken = window.localStorage.getItem('accessToken');
      // const authState = window.localStorage.getItem('authState');
      // // If we have a saved auth state (like verify_login), restore it
      // if (authState) {
      //   const parsedAuthState = JSON.parse(authState);
      //   dispatch({
      //     type: 'INITIALIZE',
      //     payload: {
      //       isAuthenticated: parsedAuthState.isAuthenticated,
      //       user: parsedAuthState.user,
      //     },
      //   });
      // }
      // // Otherwise check with the token
      // else
      if (accessToken && accessToken !== 'undefined' && accessToken !== 'null') {
        // eslint-disable-next-line no-console
        console.log('[jwt] init with token, calling /api/account/my-account/...');
        try {
          const response = await axios.get(`/api/account/my-account/${accessToken}`);
          // eslint-disable-next-line no-console
          console.log('[jwt] init response status', response.data?.status, 'user.id', response.data?.user?.id);
          if (response.data.status === 'success') {
            const adex = response.data.user;
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                user: adex,
              },
            });
          } else if (response.data.status === 'verify') {
            const adex = response.data.user;
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: 'verify',
                user: adex,
              },
            });
          } else {
            // eslint-disable-next-line no-console
            console.warn('[jwt] init falling back to unauth — server returned', response.data);
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[jwt] init network/parse error', err?.message || err);
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } else {
        // eslint-disable-next-line no-console
        console.log('[jwt] init: no accessToken in localStorage');
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }

      // Load app settings
      const res = await axios.get(`/api/website/app/setting`).catch(() => ({ data: null }));
      if (res.data) {
        dispatch({
          type: 'SETTING',
          payload: {
            setting: res.data,
          },
        });
      }
    };

    initialize();
  }, []);

  // Save auth state to localStorage whenever it changes
  useEffect(() => {
    if (state.isInitialized) {
      const authState = {
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      };
      window.localStorage.setItem('authState', JSON.stringify(authState));
    }
  }, [state.isAuthenticated, state.user, state.isInitialized]);

  const login = async (username, password) => {
    const response = await axios.post('/api/login/verify/user', {
      username,
      password,
    });
    const adex = response.data.user;
    if (response.data.status === 'verify') {
      dispatch({
        type: 'VERIFY',
        payload: {
          user: adex,
        },
      });
    } else if (response.data.status === 'verify_login') {
      dispatch({
        type: 'VERIFY_LOGIN',
        payload: {
          user: adex,
        },
      });
    } else if (response.data.status === 'success') {
      const accessToken = response.data.token;
      window.localStorage.setItem('accessToken', accessToken);
      dispatch({
        type: 'LOGIN',
        payload: {
          user: adex,
        },
      });
    }
  };

  const register = async (email, password, name, username, phone, ref, pin) => {
    const response = await axios.post('/api/register', {
      email,
      password,
      name,
      username,
      phone,
      ref,
      pin,
    });
    const adex = response.data.user;
    if (response.data.status === 'verify') {
      dispatch({
        type: 'VERIFY',
        payload: {
          user: adex,
        },
      });
    } else if (response.data.status === 'success') {
      const accessToken = response.data.token;
      window.localStorage.setItem('accessToken', accessToken);
      dispatch({
        type: 'REGISTER',
        payload: {
          user: adex,
        },
      });
    }
  };

  const verify = async (code, email) => {
    const res = await axios.post('/api/verify/user/accounct', {
      code,
      email,
    });

    if (res.data.status === 'success') {
      const accessToken = res.data.token;
      window.localStorage.setItem('accessToken', accessToken);
      window.localStorage.removeItem('authState');
      dispatch({
        type: 'CHECK',
        payload: {
          user: res.data.user,
        },
      });
    }
  };

  const verifyLogin = async (code, email) => {
    const res = await axios.post('/api/verify/user/accounct/securedpin/otp', {
      code,
      email,
    });

    if (res.data.status === 'success') {
      const { token, user } = res.data;
      window.localStorage.setItem('accessToken', token);
      window.localStorage.removeItem('authState');
      dispatch({
        type: 'CHECK',
        payload: { user },
      });
    }
  };

  const logout = async () => {
    setSession(null);
    window.localStorage.removeItem('accessToken');
    window.localStorage.removeItem('authState');
    window.localStorage.removeItem('userKey');
    dispatch({ type: 'LOGOUT' });
  };

  // Re-fetch the current user (wallet balance, KYC status, etc.) from the
  // backend. Used by the dashboard reload button.
  const refreshUser = async () => {
    const accessToken = window.localStorage.getItem('accessToken');
    if (!accessToken || accessToken === 'undefined' || accessToken === 'null') return null;
    const res = await axios.get(`/api/account/my-account/${accessToken}`);
    if (res.data?.status === 'success' && res.data?.user) {
      dispatch({ type: 'CHECK', payload: { user: res.data.user } });
      return res.data.user;
    }
    return null;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'post',
        login,
        logout,
        register,
        verify,
        verifyLogin,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
